import { Component, OnInit, Input, Output, EventEmitter, ChangeDetectorRef, ViewChild } from "@angular/core";
import { TypeManagerDecorator } from "../../../../main/type.map.service";
import { S25Util } from "../../../../util/s25-util";
import { Budget } from "../Budget";
import { S25TableComponent } from "../../../s25-table/s25.table.component";
import { Table } from "../../../s25-table/Table";
import { GenericTableRenderHTMLComponent } from "../../../s25-table/generics/generic.table.render.html.component";
import { GenericTableActionsComponent, TableAction } from "../../../s25-table/generics/generic.table.actions.component";
import { S25RequestedChangesComponent } from "../../../../standalone/s25.requested.changes.component";
import { Bind } from "../../../../decorators/bind.decorator";
import { BudgetPlanService } from "./budget.plan.service";
import { S25TableModule } from "../../../s25-table/s25.table.module";
import { S25ButtonComponent } from "../../../../standalone/s25.button.component";
import { S25PipesModule } from "../../../../pipes/s25.pipes.module";
import { S25LoadingInlineModule } from "../../../s25-loading/s25.loading.inline.module";
import { BudgetPlanModalComponent } from "./budget.plan.modal.component";
import { BudgetLineItemsComponent } from "./budget.line.items.component";
import { UserprefService } from "../../../../services/userpref.service";
import { S25PricingUtil } from "../../../s25-pricing/s25.pricing.util";
import { AlertService } from "../../../../services/alert.service";
import { CacheRepository } from "../../../../decorators/cache.decorator";

@TypeManagerDecorator("s25-ng-budget-plan")
@Component({
    selector: "s25-ng-budget-plan",
    template: `
        @if (init) {
            <div class="budget-plan-management">
                <!-- Header Section -->
                <div class="budget-plan-header">
                    <h2>Budget Plan Management</h2>
                    <p class="subtitle">Create and manage budget plans for your organization</p>
                </div>

                <!-- Loading State -->
                @if (isLoading) {
                    <s25-ng-loading-inline-static [text]="'Loading budget plans...'"></s25-ng-loading-inline-static>
                }

                <!-- Error State -->
                @if (error) {
                    <div class="error-message">
                        <p>{{ error }}</p>
                        <s25-ng-button
                            [type]="'outline'"
                            [buttonText]="'Retry'"
                            [onClick]="loadBudgetPlans"
                        ></s25-ng-button>
                    </div>
                }

                <!-- Budget Plans Table -->
                @if (!isLoading && !error) {
                    <div class="budget-plans-section">
                        <div class="section-header">
                            <h3>Budget Plans</h3>
                            <s25-ng-button
                                [type]="'primary'"
                                [buttonText]="'Create Budget Plan'"
                                [onClick]="createBudgetPlan"
                                [isLoading]="isCreatingPlan"
                                [loadingText]="'Creating...'"
                            ></s25-ng-button>
                        </div>

                        <s25-ng-table
                            [dataSource]="tableConfig"
                            [columnSortable]="true"
                            [hasFilter]="true"
                            [hasRefresh]="true"
                            [hasSelect]="true"
                            [hasSelectAll]="true"
                            [hasExpandableRows]="true"
                            [(selected)]="selectedPlanIds"
                            (selectedChange)="onSelectionChange($event)"
                        ></s25-ng-table>
                    </div>
                }

                <!-- Create/Edit Budget Plan Modal -->
                <s25-ng-budget-plan-modal
                    #budgetPlanModal
                    [orgId]="orgId"
                    [planId]="planId"
                    (planSaved)="onPlanSaved($event)"
                    (planCancelled)="onPlanCancelled()"
                ></s25-ng-budget-plan-modal>
            </div>
        }
    `,
    styles: `
        .budget-plan-management {
            max-width: 1200px;
            margin: 0 auto;
            padding: 2rem;
            background: white;
            border-radius: 8px;
            box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
        }

        .budget-plan-header {
            text-align: center;
            margin-bottom: 2rem;
            padding-bottom: 1rem;
            border-bottom: 1px solid #e5e5e5;
        }

        .budget-plan-header h2 {
            margin: 0 0 0.5rem 0;
            color: #333;
            font-size: 1.8rem;
        }

        .budget-plan-header .subtitle {
            margin: 0;
            color: #666;
            font-size: 1rem;
        }

        .error-message {
            text-align: center;
            padding: 1.5rem;
            background: #fff3f3;
            border: 1px solid #ffcdd2;
            border-radius: 8px;
            margin: 1rem 0;
        }

        .error-message p {
            color: #d32f2f;
            margin-bottom: 1rem;
        }

        .budget-plans-section {
            margin-top: 2rem;
        }

        .section-header {
            display: flex;
            justify-content: space-between;
            align-items: center;
            margin-bottom: 1.5rem;
            padding-bottom: 0.8rem;
            border-bottom: 1px solid #e5e5e5;
        }

        .section-header h3 {
            margin: 0;
            color: #333;
            font-size: 1.4rem;
            font-weight: 600;
        }

        .bulk-actions {
            display: flex;
            justify-content: flex-end;
            gap: 1rem;
            margin-top: 1.5rem;
            padding-top: 1rem;
            border-top: 1px solid #e5e5e5;
        }

        .selected-count {
            font-weight: 600;
            color: #333;
        }

        /* Status badges */
        :host ::ng-deep .status-badge {
            display: inline-block;
            padding: 0.25rem 0.75rem;
            border-radius: 12px;
            font-size: 0.85rem;
            font-weight: 600;
            text-align: center;
            white-space: nowrap;
        }

        :host ::ng-deep .status-draft {
            background: #e3f2fd;
            color: #1976d2;
        }

        :host ::ng-deep .status-in-progress {
            background: #fff3e0;
            color: #f57c00;
        }

        :host ::ng-deep .status-approved {
            background: #e8f5e9;
            color: #388e3c;
        }

        :host ::ng-deep .status-denied {
            background: #ffebee;
            color: #d32f2f;
        }

        :host ::ng-deep .status-changes-requested {
            background: #fce4ec;
            color: #c2185b;
        }

        :host ::ng-deep .status-unknown {
            background: #f5f5f5;
            color: #757575;
        }

        @media (max-width: 768px) {
            .budget-plan-management {
                padding: 1rem;
                margin: 0;
                border-radius: 0;
            }
        }
    `,
    standalone: true,
    imports: [S25TableModule, S25ButtonComponent, S25PipesModule, S25LoadingInlineModule, BudgetPlanModalComponent],
    providers: [BudgetPlanService],
})
export class BudgetPlanComponent implements OnInit {
    @Input() orgId: number;
    @Input() planId?: number; // For edit mode

    @Output() planSaved = new EventEmitter<Budget.BudgetPlan>();
    @Output() planCancelled = new EventEmitter<void>();

    @ViewChild("budgetPlanModal") budgetPlanModal: BudgetPlanModalComponent;
    @ViewChild(S25TableComponent) tableComponent: S25TableComponent;

    init = false;
    isLoading = false;
    isCreatingPlan = false;
    error: string | null = null;

    budgetPlans: Budget.BudgetPlan[] = [];
    selectedPlanIds: Set<number> = new Set();
    selectedPlans: Budget.BudgetPlan[] = [];

    tableConfig: Table.DataSource;

    constructor(private cd: ChangeDetectorRef) {}

    ngOnInit(): void {
        this.init = true;
        this.initTableConfig();
        this.loadBudgetPlans();
    }

    private initTableConfig(): void {
        const columns: Table.Column[] = [
            { id: "name", header: "Plan Name", minWidth: 150 },
            { id: "planType", header: "Plan Type", width: 120 },
            { id: "status", header: "Status", width: 130 },
            { id: "allocatedAmount", header: "Allocated", width: 120, align: "right" },
            { id: "description", header: "Description", minWidth: 200 },
            { id: "actions", header: "Actions", width: 200, sortable: false },
        ];

        this.tableConfig = {
            type: "unpaginated",
            columns: columns,
            dataSource: this.getBudgetPlans,
        };
    }

    @Bind
    async getBudgetPlans(query: Table.UnpaginatedQuery): Promise<Table.DataSourceResponse> {
        try {
            if (query.forceRefresh) {
                CacheRepository.invalidateByService("BudgetPlanService", "getBudgetPlans");
            }
            const dateFormat = await UserprefService.getS25Dateformat();
            const [data, error] = await S25Util.Maybe(BudgetPlanService.getBudgetPlans(this.orgId));

            if (error) {
                throw error;
            }

            this.budgetPlans = data || [];

            // Load line items for each budget plan
            const rowsWithLineItems = await Promise.all(
                this.budgetPlans.map(async (plan) => {
                    const statusInfo = this.getStatusInfo(plan);
                    // Handle both number and parsed value format
                    const allocatedAmount =
                        typeof plan.allocatedAmount === "number"
                            ? plan.allocatedAmount
                            : (plan.allocatedAmount?.parsedValue ?? 0);

                    // Determine if budget is a draft
                    const isDraft = !plan.workflowStatus;
                    const canEdit = isDraft || plan.workflowStatus === "REQUESTED_CHANGES";
                    const hasRequestedChanges = plan.workflowStatus === "REQUESTED_CHANGES";

                    // Build className for row styling
                    let rowClassName = "";
                    if (hasRequestedChanges) {
                        rowClassName = "has-requested-changes";
                    }

                    return {
                        id: plan.budgetPlanId,
                        name: plan.name,
                        expandable: true,
                        className: rowClassName,
                        cells: {
                            name: { text: plan.name },
                            planType: { text: plan.planType },
                            status: {
                                component: GenericTableRenderHTMLComponent,
                                inputs: {
                                    text: `<span class="status-badge status-${statusInfo.class}">${statusInfo.label}</span>`,
                                    isHtml: true,
                                },
                                textValue: statusInfo.label,
                            },
                            allocatedAmount: {
                                text: S25PricingUtil.formatCurrency(allocatedAmount, plan.currency),
                            },
                            description: { text: plan.description || "" },
                            actions: {
                                component: GenericTableActionsComponent,
                                inputs: {
                                    actions: this.getActions(plan, isDraft, canEdit),
                                },
                            },
                        },
                        expandedContent: {
                            component: BudgetLineItemsComponent,
                            inputs: {
                                budgetPlan: plan,
                            },
                            outputs: {},
                        },
                        data: plan, // Store plan data for template access
                    } as Table.Row;
                }),
            );

            return {
                rows: rowsWithLineItems,
                totalRows: rowsWithLineItems.length,
            };
        } catch (error) {
            console.error("Error loading budget plans:", error);
            return {
                rows: [],
                totalRows: 0,
            };
        }
    }

    async loadBudgetPlans(): Promise<void> {
        this.isLoading = true;
        this.error = null;
        this.cd.detectChanges();

        try {
            await this.tableComponent?.refresh(true);
        } catch (error) {
            this.error = error instanceof Error ? error.message : "An unexpected error occurred";
        } finally {
            this.isLoading = false;
            this.cd.detectChanges();
        }
    }

    @Bind
    async createBudgetPlan(): Promise<void> {
        this.isCreatingPlan = true;
        this.cd.detectChanges();

        try {
            this.budgetPlanModal.openCreateModal();
        } catch (error) {
            S25Util.showError(error, "Failed to open budget plan form");
        } finally {
            this.isCreatingPlan = false;
            this.cd.detectChanges();
        }
    }

    async editBudgetPlan(plan: Budget.BudgetPlan): Promise<void> {
        this.budgetPlanModal.openEditModal(plan);
    }

    @Bind
    async submitBudgetPlan(plan: Budget.BudgetPlan): Promise<void> {
        try {
            const confirmed = await AlertService.confirm(
                `Are you sure you want to submit "${plan.name}" for approval? You will not be able to edit it after submission.`,
            );

            if (!confirmed) return;

            const [_, error] = await S25Util.Maybe(BudgetPlanService.submitBudgetPlanForApproval(plan.budgetPlanId));

            if (error) {
                S25Util.showError(error, "Failed to submit budget plan");
                return;
            }

            alert("Budget plan submitted successfully");
            await this.tableComponent?.refresh(true);
        } catch (error) {
            S25Util.showError(error, "Failed to submit budget plan");
        }
    }

    private getActions(plan: Budget.BudgetPlan, isDraft: boolean, canEdit: boolean): TableAction[] {
        const actions: TableAction[] = [];
        const hasRequestedChanges = plan.workflowStatus === "REQUESTED_CHANGES";

        if (canEdit) {
            actions.push({
                label: "Edit",
                type: "outline",
                onClick: () => this.editBudgetPlan(plan),
                visible: true,
            });
        }

        if (isDraft) {
            actions.push({
                label: "Submit for Approval",
                type: "primary",
                onClick: () => this.submitBudgetPlan(plan),
                visible: true,
            });
        }

        if (hasRequestedChanges) {
            actions.push({
                label: "Resubmit",
                type: "primary",
                onClick: () => this.submitBudgetPlan(plan),
                visible: true,
            });
        }

        return actions;
    }

    onSelectionChange(selectedIds: Set<number>): void {
        this.selectedPlanIds = selectedIds;
        this.selectedPlans = this.budgetPlans.filter((plan) => selectedIds.has(plan.budgetPlanId));
    }

    onPlanSaved(plan: Budget.BudgetPlan): void {
        this.planSaved.emit(plan);
        // Refresh the table after a plan is saved
        this.tableComponent?.refresh(true);
    }

    onPlanCancelled(): void {
        this.planCancelled.emit();
    }

    private getStatusInfo(plan: Budget.BudgetPlan): { label: string; class: string } {
        if (!plan.workflowStatus) {
            return { label: "Draft", class: "draft" };
        }

        switch (plan.workflowStatus) {
            case "PENDING":
                return { label: "Awaiting Approval", class: "awaiting-approval" };
            case "IN_PROGRESS":
                return { label: "In Progress", class: "in-progress" };
            case "COMPLETED":
                return { label: "Approved", class: "approved" };
            case "DENIED":
                return { label: "Denied", class: "denied" };
            case "REQUESTED_CHANGES":
                return { label: "Changes Requested", class: "changes-requested" };
            default:
                return { label: "Unknown", class: "unknown" };
        }
    }
}
